import axios from 'axios';
import { LOG_LEVEL_INFO, LOG_SOURCE_FE } from '../constants/logs';
import { LOG_TO_KIBANA } from '../constants/api.fes';

/**
 * This function send logs to Kibana
 * @param template - mandatory parameter with log message, ex.: 'Error while sending request to the server (Method: %s , URL: %s)',
 * @param params - not mandatory parameter, ex.: [error.method, error.url]
 * @param extraParams - not mandatory parameter with extra data which is translated to a string without ability to filter on it in Kibana
 * @param logLevel - not mandatory could be one of: LOG_LEVEL_ERROR, LOG_LEVEL_WARNING, LOG_LEVEL_INFO
 * @param source - not mandatory, marks the origin of the report for the backend and is filterable in Kibana, ex.: LOG_SOURCE_FE_REQUEST_FAILED, defaults to LOG_SOURCE_FE
 * @param responseCode - not mandatory, HTTP status of a failed response, sent only when defined
 * @param rootLevel - not mandatory parameter of root level data placed on the root of the log, ex: customer_id, with ability to filter in Kibana, make dashboards in Grafana
 */

const CANCELED_ERROR_NAMES = ['CanceledError', 'AbortError'];

export const isRequestCanceled = err =>
  axios.isCancel(err) || err?.code === 'ERR_CANCELED' || CANCELED_ERROR_NAMES.includes(err?.name);

export const buildRequestUrl = (baseURL = '', url = '') =>
  /^https?:\/\//i.test(url) ? url : `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;

const MAX_LOGGED_RESPONSE_LENGTH = 2000;

const toTruncatedText = value => {
  if (value === undefined || value === null) return undefined;
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  return text.length > MAX_LOGGED_RESPONSE_LENGTH ? `${text.slice(0, MAX_LOGGED_RESPONSE_LENGTH)}…` : text;
};

// Request headers, request body and query string are left out on purpose: they carry tenant ids and customer data
export const toLoggableRequestError = err => {
  const config = err?.config || {};
  return {
    method: config.method,
    url: buildRequestUrl(config.baseURL, config.url).split('?')[0],
    status: err?.response?.status,
    statusText: err?.response?.statusText,
    code: err?.code,
    name: err?.name,
    message: err?.message,
    responseData: toTruncatedText(err?.response?.data)
  };
};

export const sendLogToKibana = ({
  template,
  params = [],
  extraParams = {},
  logLevel = LOG_LEVEL_INFO,
  rootLevel = {},
  source = LOG_SOURCE_FE,
  responseCode
}) => {
  if (process.env.NODE_ENV === 'production') {
    axios.post(LOG_TO_KIBANA(logLevel), {
      template,
      params: params,
      extraParams: extraParams,
      rootLevel: rootLevel,
      source,
      ...(Number.isInteger(responseCode) && { responseCode })
    });
  }
};

/**
 * This function runs a validation function and provide console log + Kibana error if the validation failed.
 * @param validFunction - a function that is doing the validation logic
 * @param message - message to write into the console + Kibana log.
 * @returns {*|boolean}
 */
export default (validFunction, message) => {
  const isValid = typeof validFunction === 'function' ? validFunction() : false;

  if (!isValid) {
    const logStyle = 'background: #222; color: #bada55';

    // eslint-disable-next-line no-console
    console.log(`%cValidation failed with this message: ${message}`, logStyle);

    sendLogToKibana({ template: message, logLevel: LOG_LEVEL_INFO });
  }
  return isValid;
};
